// src/features/scheduleBuilder/slices/claimRequestTotals/slice.ts
import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';
import { fetchJSON } from 'api/fetch';
import { RootState } from 'initializers/types';

import { PARENT_SLICE_NAME, SLICE_NAME } from './constants';
import {
  ClaimRequestTotals,
  ClaimRequestTotalsPayload,
  ClaimRequestTotalsPayloadItem,
  ClaimRequestTotalsState,
} from './types';

export const fetchClaimRequestTotals = createAsyncThunk<
  ClaimRequestTotalsPayload,
  string
>(`${SLICE_NAME}/fetchById`, async (id: string) => {
  const resp = await fetchJSON(
    `/schedule_builder/labor/${id}/claim_request_totals`
  );
  return resp;
});

const claimRequestTotalsAdapter = createEntityAdapter<ClaimRequestTotals>();

export const initialState: ClaimRequestTotalsState =
  claimRequestTotalsAdapter.getInitialState({
    status: 'idle',
    error: undefined,
  });

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchClaimRequestTotals.pending, state => {
      state.status = 'pending';
    });
    builder.addCase(fetchClaimRequestTotals.fulfilled, (state, action) => {
      const items = action.payload.data
        .filter(
          (item: ClaimRequestTotalsPayloadItem) =>
            item.type === 'claimRequestTotals'
        )
        .map((item: ClaimRequestTotalsPayloadItem) => ({
          id: item.id,
          type: item.type,
          hours: item.attributes.hours,
          cost: item.attributes.cost,
          overtimes: item.attributes.overtimes,
          totalOvertime: item.attributes.totalOvertime,
          claimRequestId: item.relationships.claimRequest.data.id,
        }));
      claimRequestTotalsAdapter.setAll(state, items);
      state.status = 'succeeded';
    });
    builder.addCase(fetchClaimRequestTotals.rejected, (state, action) => {
      state.status = 'failed';
      state.error =
        action.error.message || 'Failed to fetch claim request totals by id';
    });
  },
});

export const reducer = slice.reducer;

const getScheduleBuilderState = (state: RootState) =>
  state.get(PARENT_SLICE_NAME);

export const {
  selectById: selectClaimRequestTotalsById,
  selectIds: selectClaimRequestTotalsIds,
  selectEntities: selectClaimRequestTotalsEntities,
  selectAll: selectAllClaimRequestTotals,
  selectTotal: selectTotalClaimRequestTotals,
} = claimRequestTotalsAdapter.getSelectors<RootState>(
  state => getScheduleBuilderState(state)?.[SLICE_NAME] || initialState
);

export const selectClaimRequestTotalsStatus = (state: RootState) =>
  getScheduleBuilderState(state)?.[SLICE_NAME]?.status || 'idle';

export const selectClaimRequestTotalsAttribute = <
  T extends keyof ClaimRequestTotals
>({
  state,
  attribute,
  id,
}: {
  state: RootState;
  attribute: T;
  id: string;
}): ClaimRequestTotals[T] | undefined => {
  const claimRequestTotals = selectClaimRequestTotalsById(state, id);
  return claimRequestTotals ? claimRequestTotals[attribute] : undefined;
};

export const selectClaimRequestTotalsFetchIsPending = (
  state: RootState
): boolean => selectClaimRequestTotalsStatus(state) === 'pending';
